import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Modal } from '@/components/ui';
import { useAuth } from '../../../contexts/AuthContext';
import { hiredEquipmentPoService } from '../../../services/hiredEquipmentPoService';
import { taskService } from '../../../services/taskService';
import { vendorService } from '../../../services/vendorService';
import {
  PO_DISABLED_CHOOSER_BEHAVIOR,
  PO_DISABLED_TOOLTIP,
  PO_ENABLED,
} from '../config/poEnabled';
import { heExcludedStandingTypes } from '../types';
import { usePermissions } from '../../../hooks/usePermissions';
import type {
  HiredEquipmentAnchor,
  HiredEquipmentEntryMethod,
  HiredEquipmentPurchaseOrder,
  TaskOption,
  VendorOption,
} from '../types';
import { canContinueToGrid, continueToGridBlocker, defaultEntryMethod } from '../utils/anchorValidation';
import { buildHiredEquipmentAnchor } from '../utils/buildAnchor';
import { resolveDefaultServiceAreaId } from '../utils/defaultServiceArea';
import { describeLoadError } from '../../../utils/loadError';
import { formatTaskLabel, isStandingRoutineTask, toTaskOption } from '../utils/poPrefill';
import EntryMethodCard, { ENTRY_METHOD_OPTIONS } from './EntryMethodCard';
import EntryMethodFields from './EntryMethodFields';
import HiredEquipmentTaskModals, { type HiredEquipmentTaskModalRequest } from './HiredEquipmentTaskModals';

export interface NewHiredEquipmentModalProps {
  show: boolean;
  onHide: () => void;
  /** Called with the locked anchor. Parent should persist and open the grid. */
  onContinue: (anchor: HiredEquipmentAnchor) => void;
  /**
   * Override HE-P04. Defaults to {@link PO_ENABLED}.
   * Pass `false` in tests / when the workspace flag is off.
   */
  poEnabled?: boolean;
}

const emptyForm = (poEnabled: boolean) => ({
  entryMethod: defaultEntryMethod(poEnabled) as HiredEquipmentEntryMethod,
  vendorId: '' as number | '',
  poId: '' as number | '',
  selectedTask: null as TaskOption | null,
  prePopulate: true,
});

const NewHiredEquipmentModal: React.FC<NewHiredEquipmentModalProps> = ({
  show,
  onHide,
  onContinue,
  poEnabled = PO_ENABLED,
}) => {
  const { currentUser } = useAuth();
  const { hasPermission } = usePermissions();
  const canCreateRoutineTask = hasPermission('routine:specific_create');
  const [form, setForm] = useState(() => emptyForm(poEnabled));
  /** C2: the Standing Routine task picked in By Task, while its Specific task is being created. */
  const [standingBaseTask, setStandingBaseTask] = useState<TaskOption | null>(null);
  const standingTaskRequest = useMemo<HiredEquipmentTaskModalRequest | null>(
    () => (standingBaseTask ? { kind: 'create', rowClientId: 'he-anchor', baseTask: standingBaseTask } : null),
    [standingBaseTask],
  );
  const [vendors, setVendors] = useState<VendorOption[]>([]);
  const [vendorsLoading, setVendorsLoading] = useState(false);
  const [purchaseOrders, setPurchaseOrders] = useState<HiredEquipmentPurchaseOrder[]>([]);
  const [posLoading, setPosLoading] = useState(false);
  const [vendorsError, setVendorsError] = useState<string | null>(null);
  const [posError, setPosError] = useState<string | null>(null);
  const inferredServiceAreaId = resolveDefaultServiceAreaId(currentUser);

  const poCardHidden = !poEnabled && PO_DISABLED_CHOOSER_BEHAVIOR === 'hide';
  const poCardDisabled = !poEnabled && PO_DISABLED_CHOOSER_BEHAVIOR === 'disable';

  const visibleMethods = useMemo(
    () => ENTRY_METHOD_OPTIONS.filter((option) => !(option.method === 'purchase_order' && poCardHidden)),
    [poCardHidden],
  );

  const resetForm = useCallback(() => {
    setForm(emptyForm(poEnabled));
    setPurchaseOrders([]);
  }, [poEnabled]);

  useEffect(() => {
    if (!show) return;
    resetForm();
  }, [show, resetForm]);

  useEffect(() => {
    if (!show) return;
    let cancelled = false;

    const loadVendors = async () => {
      setVendorsLoading(true);
      setVendorsError(null);
      try {
        const rows = await vendorService.getAllVendors({
          status: 'ACTIVE',
          ...(inferredServiceAreaId != null ? { serviceAreaId: inferredServiceAreaId } : {}),
        });
        if (cancelled) return;
        setVendors(rows.map((row) => ({ id: row.id, name: row.name })));
        setForm((prev) => {
          if (prev.vendorId === '') return prev;
          const stillPresent = rows.some((row) => row.id === prev.vendorId);
          return stillPresent ? prev : { ...prev, vendorId: '', poId: '' };
        });
      } catch (error) {
        console.error('Failed to load vendors for hired equipment chooser', error);
        if (!cancelled) {
          setVendors([]);
          setVendorsError(describeLoadError(error, 'vendors').message);
        }
      } finally {
        if (!cancelled) setVendorsLoading(false);
      }
    };

    loadVendors();
    return () => {
      cancelled = true;
    };
  }, [show, inferredServiceAreaId]);

  useEffect(() => {
    if (!show || !form.vendorId || form.entryMethod !== 'purchase_order' || !poEnabled) {
      setPurchaseOrders([]);
      setPosError(null);
      return;
    }
    let cancelled = false;
    const load = async () => {
      setPosLoading(true);
      setPosError(null);
      try {
        const rows = await hiredEquipmentPoService.getActivePosForVendor(
          form.vendorId as number,
          inferredServiceAreaId,
        );
        if (!cancelled) setPurchaseOrders(rows);
      } catch (error) {
        console.error('Failed to load purchase orders for vendor', form.vendorId, error);
        if (!cancelled) {
          setPurchaseOrders([]);
          setPosError(describeLoadError(error, 'purchase orders').message);
        }
      } finally {
        if (!cancelled) setPosLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [show, form.vendorId, form.entryMethod, poEnabled, inferredServiceAreaId]);

  const handleSelectMethod = (method: HiredEquipmentEntryMethod) => {
    setForm((prev) => ({
      ...prev,
      entryMethod: method,
      // Keep vendor; drop method-specific fields that don't apply.
      poId: method === 'purchase_order' ? prev.poId : '',
      selectedTask: method === 'task' ? prev.selectedTask : null,
      prePopulate: method === 'purchase_order' ? prev.prePopulate : false,
    }));
  };

  const handleVendorChange = (id: number | '') => {
    setForm((prev) => ({ ...prev, vendorId: id, poId: '' }));
  };

  const handleCancel = () => {
    resetForm();
    onHide();
  };

  const continueEnabled = canContinueToGrid(
    form.entryMethod,
    {
      vendorId: form.vendorId === '' ? null : form.vendorId,
      poId: form.poId === '' ? null : form.poId,
      taskId: form.selectedTask?.id ?? null,
    },
    poEnabled,
  );

  const continueBlocker = continueToGridBlocker(
    form.entryMethod,
    {
      vendorId: form.vendorId === '' ? null : form.vendorId,
      poId: form.poId === '' ? null : form.poId,
      taskId: form.selectedTask?.id ?? null,
    },
    poEnabled,
  );

  const handleContinue = () => {
    if (!continueEnabled) return;
    const vendor = vendors.find((item) => item.id === form.vendorId);
    const po = purchaseOrders.find((item) => item.id === form.poId);
    const taskLabel = form.selectedTask ? formatTaskLabel(form.selectedTask) : null;
    const anchor = buildHiredEquipmentAnchor({
      entryMethod: form.entryMethod,
      vendorId: form.vendorId === '' ? null : form.vendorId,
      vendorName: vendor?.name ?? null,
      poId: form.poId === '' ? null : form.poId,
      poNumber: po?.poNumber ?? null,
      poDescription: po?.description ?? null,
      taskId: form.selectedTask?.id ?? null,
      taskLabel,
      serviceAreaId: inferredServiceAreaId,
      prePopulate: form.prePopulate,
      poEnabled,
    });
    onContinue(anchor);
  };

  const fetchTasks = useCallback(async (query: string): Promise<TaskOption[]> => {
    // H2: only tasks that can receive effort (Active and Active – Marked as Complete – the list is
    // server configuration). C2: Standing Routine tasks are offered when the user can create the
    // Specific task from one; Standing Quantified never. Overhead stays pickable. Must match what
    // the grid's own picker sends (useHiredEquipmentGridData.searchTasks).
    const tasks = await taskService.searchActiveTasks(query, 20, {
      excludeStandingTypes: heExcludedStandingTypes(canCreateRoutineTask),
      effortEligibleOnly: true,
    });
    return tasks.map(toTaskOption);
  }, [canCreateRoutineTask]);

  const handleTaskChange = (task: TaskOption | null) => {
    // C2: a Standing Routine task is a template – create the Specific task first, then anchor to it.
    if (task && canCreateRoutineTask && isStandingRoutineTask(task)) {
      setStandingBaseTask(task);
      return;
    }
    setForm((prev) => ({ ...prev, selectedTask: task }));
  };

  return (
    <Modal show={show} onHide={handleCancel} size="xl" autoHeight backdrop="static">
      <Modal.Header closeButton onHide={handleCancel}>
        <Modal.Title>New Hired Equipment Submission</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="tw-mb-3 tw-text-base tw-font-semibold tw-text-content">
          How do you want to enter this?
        </p>
        <div
          role="radiogroup"
          aria-label="How do you want to enter this?"
          className="tw-mb-4 tw-grid tw-grid-cols-1 tw-gap-3 md:tw-grid-cols-3"
        >
          {visibleMethods.map((option) => (
            <EntryMethodCard
              key={option.method}
              option={option}
              selected={form.entryMethod === option.method}
              disabled={option.method === 'purchase_order' && poCardDisabled}
              disabledReason={option.method === 'purchase_order' && poCardDisabled ? PO_DISABLED_TOOLTIP : undefined}
              onSelect={handleSelectMethod}
            />
          ))}
        </div>

        {/* L12: hold the method-specific area at a fixed minimum height so switching method
            does not resize the window or move the cards above it. */}
        <div className="tw-min-h-[9.5rem]" data-testid="he-method-fields">
        <EntryMethodFields
          entryMethod={form.entryMethod}
          poEnabled={poEnabled}
          vendors={vendors}
          vendorsLoading={vendorsLoading}
          vendorsError={vendorsError}
          vendorId={form.vendorId}
          onVendorChange={handleVendorChange}
          purchaseOrders={purchaseOrders}
          posLoading={posLoading}
          posError={posError}
          poId={form.poId}
          onPoChange={(id) => setForm((prev) => ({ ...prev, poId: id }))}
          selectedTask={form.selectedTask}
          onTaskChange={handleTaskChange}
          fetchTasks={fetchTasks}
          prePopulate={form.prePopulate}
          onPrePopulateChange={(checked) => setForm((prev) => ({ ...prev, prePopulate: checked }))}
        />
        </div>
      </Modal.Body>
      <Modal.Footer disabledReason={continueBlocker}>
        <Button variant="secondary" onClick={handleCancel}>
          Cancel
        </Button>
        <Button
          variant="primary"
          disabled={!continueEnabled}
          disabledReason={continueBlocker ?? undefined}
          onClick={handleContinue}
        >
          Continue to Grid
        </Button>
      </Modal.Footer>
      {standingTaskRequest && (
        <HiredEquipmentTaskModals
          request={standingTaskRequest}
          onClose={() => setStandingBaseTask(null)}
          onTaskCreated={(_rowClientId, created) => {
            setForm((prev) => ({ ...prev, selectedTask: created }));
            setStandingBaseTask(null);
          }}
          onTaskUpdated={() => undefined}
          availableTypes={['Routine']}
        />
      )}
    </Modal>
  );
};

export default NewHiredEquipmentModal;
